import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaBolt,
  FaBroom,
  FaCalendarAlt,
  FaChartBar,
  FaChartLine,
  FaChevronDown,
  FaCheckCircle,
  FaCogs,
  FaExclamationTriangle,
  FaGlobeAmericas,
  FaRegHandPaper,
  FaStopwatch,
  FaTasks,
  FaTimesCircle,
  FaWpforms,
} from "react-icons/fa";
import api from "../api";
import { useAuth } from "../AuthContext";
import ProcessRunner from "./ProcessRunner";
import Agenda from "./Agenda";
import { extractResponsibleTargets } from "../utils/responsibles";

const BLUE_BACKGROUNDS = [
  "from-blue-50 to-blue-100",
  "from-sky-50 to-blue-100",
  "from-blue-100 to-blue-200",
  "from-sky-100 to-blue-200",
];

const METRIC_CARD_THEMES = {
  alert: {
    background: "from-amber-50 to-amber-100",
    textColor: "text-amber-900",
    labelColor: "text-amber-600",
    descriptionColor: "text-amber-700",
    iconWrapper: "bg-amber-100 text-amber-600",
    focusRing: "focus-visible:ring-amber-500",
    activeRing: "ring-amber-400",
    inactiveRing: "ring-amber-100",
  },
  late: {
    background: "from-red-50 to-red-100",
    textColor: "text-red-900",
    labelColor: "text-red-600",
    descriptionColor: "text-red-700",
    iconWrapper: "bg-red-100 text-red-600",
    focusRing: "focus-visible:ring-red-500",
    activeRing: "ring-red-400",
    inactiveRing: "ring-red-100",
  },
};

const getMetricCardTheme = (cardId, index) => {
  if (METRIC_CARD_THEMES[cardId]) {
    return METRIC_CARD_THEMES[cardId];
  }

  return {
    background: BLUE_BACKGROUNDS[index % BLUE_BACKGROUNDS.length],
    textColor: "text-blue-900",
    labelColor: "text-blue-500",
    descriptionColor: "text-blue-600",
    iconWrapper: "bg-blue-100 text-blue-600",
    focusRing: "focus-visible:ring-blue-500",
    activeRing: "ring-blue-400",
    inactiveRing: "ring-blue-100",
  };
};

const formatDuration = (milliseconds) => {
  if (!milliseconds || Number.isNaN(milliseconds)) return "-";
  const totalHours = Math.max(milliseconds / (1000 * 60 * 60), 0);
  if (totalHours >= 48) {
    return `${Math.round(totalHours / 24)}d`;
  }
  if (totalHours >= 1) {
    return `${Math.round(totalHours)}h`;
  }
  return "<1h";
};

const formatNumber = (value) => {
  if (value == null) return "-";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return numeric.toLocaleString("pt-BR");
};

const COMPLETED_ANALYTICS_DAYS = 365;
const COMPLETED_ANALYTICS_LIMIT = 5000;

const normalizeId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return null;
};

const toNormalizedArray = (values) => {
  if (!Array.isArray(values)) return [];
  const unique = new Set();
  values.forEach((value) => {
    const normalized = normalizeId(value);
    if (normalized) unique.add(normalized);
  });
  return Array.from(unique);
};

const parseInstanceDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const inferStepStartDate = (instance) => {
  if (!instance) return null;
  const candidateKeys = [
    "currentStepStartedAt",
    "currentStepStartAt",
    "currentStepStartDate",
    "currentStepAssignedAt",
    "currentStepEnteredAt",
    "currentStepArrivalDate",
    "startedAt",
  ];

  for (const key of candidateKeys) {
    const parsed = parseInstanceDate(instance[key]);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const getInstanceKey = (instance) => {
  if (!instance) return null;
  if (instance.id !== undefined && instance.id !== null) {
    return String(instance.id);
  }
  if (instance.activityId !== undefined && instance.activityId !== null) {
    return String(instance.activityId);
  }
  if (instance.processInstanceId !== undefined && instance.processInstanceId !== null) {
    return String(instance.processInstanceId);
  }
  return null;
};

const NO_DEADLINE_LABEL = "Sem prazo";

const ALERT_WINDOW_MS = 24 * 60 * 60 * 1000;

const getDeadlineStatus = (instance) => {
  if (!instance.currentStepDeadline) {
    return { label: NO_DEADLINE_LABEL, color: "text-sky-600" };
  }
  const deadline = new Date(instance.currentStepDeadline);
  const diff = deadline.getTime() - Date.now();
  if (diff < 0) {
    return { label: "Prazo excedido", color: "text-rose-600" };
  }
  if (diff < ALERT_WINDOW_MS) {
    return { label: "Menos de 24h", color: "text-amber-600" };
  }
  const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
  return { label: `Vence em ${days}d`, color: "text-emerald-600" };
};

export default function HomeDashboard({ openTab, refreshKey }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [counts, setCounts] = useState({ total: 0, overdue: 0, open: 0, completed: 0 });
  const [activePanel, setActivePanel] = useState("overview");
  const [panelRefreshToken, setPanelRefreshToken] = useState(0);
  const [globalOpenItems, setGlobalOpenItems] = useState([]);
  const [globalCompletedItems, setGlobalCompletedItems] = useState([]);
  const [globalProcesses, setGlobalProcesses] = useState([]);
  const [globalForms, setGlobalForms] = useState([]);
  const [recentDays, setRecentDays] = useState(7);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedError, setAdvancedError] = useState("");
  const [advancedWarnings, setAdvancedWarnings] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [todayMessages, setTodayMessages] = useState([]);
  const [taskFilter, setTaskFilter] = useState("all");
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(() => new Set());

  const extractResponsible = useCallback(extractResponsibleTargets, []);

  const evaluateTaskDeadline = useCallback((instance) => {
    const deadline = parseInstanceDate(instance?.currentStepDeadline);
    if (!deadline) {
      return { status: "onTime", remaining: Number.POSITIVE_INFINITY };
    }

    const deadlineTime = deadline.getTime();
    if (!Number.isFinite(deadlineTime)) {
      return { status: "onTime", remaining: Number.POSITIVE_INFINITY };
    }

    const now = Date.now();
    const remaining = deadlineTime - now;
    if (remaining < 0) {
      return { status: "late", remaining };
    }

    const startedAt = inferStepStartDate(instance);
    if (startedAt) {
      const totalDuration = deadlineTime - startedAt.getTime();
      if (totalDuration > 0) {
        const alertThreshold = totalDuration * 0.2;
        if (remaining <= alertThreshold) {
          return { status: "alert", remaining };
        }
      }
    }

    if (remaining <= ALERT_WINDOW_MS) {
      return { status: "alert", remaining };
    }

    return { status: "onTime", remaining };
  }, []);

  const refreshPanelData = useCallback(() => {
    setPanelRefreshToken((prev) => prev + 1);
  }, []);

  const handlePanelSelect = useCallback(
    (panelId) => {
      setActivePanel((current) => {
        refreshPanelData();
        if (current === panelId) {
          return current;
        }
        return panelId;
      });
    },
    [refreshPanelData]
  );

  const toggleGroupExpansion = useCallback((groupKey) => {
    if (!groupKey) {
      return;
    }

    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (activePanel !== "overview") return;
    let cancelled = false;
    setTasksLoading(true);
    api
      .get("/processes/instances/open")
      .then((res) => {
        if (!cancelled) setItems(res.data);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) {
          setTasksLoading(false);
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new window.CustomEvent("bpm:home-tasks-priority-ready", {
                detail: { source: "HomeDashboard", at: Date.now() },
              })
            );
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activePanel, panelRefreshToken, refreshKey]);
  useEffect(() => {
    if (activePanel !== "overview") return;
    let cancelled = false;
    api
      .get("/processes/instances/my-counts")
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setCounts({
          total: Number(data.total) || 0,
          overdue: Number(data.overdue) || 0,
          open: Number(data.open) || 0,
          completed:
            Number(data.completed) ||
            Math.max((Number(data.total) || 0) - (Number(data.open) || 0), 0),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setCounts({ total: 0, overdue: 0, open: 0, completed: 0 });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activePanel, panelRefreshToken, refreshKey]);

  const loadCalendarHighlights = useCallback(async () => {
    setCalendarLoading(true);
    setCalendarError("");
    try {
      const res = await api.get("/calendar/summary");
      const data = res.data || {};
      setTodayMessages(Array.isArray(data.todayMessages) ? data.todayMessages : []);
    } catch (error) {
      setTodayMessages([]);
      setCalendarError(
        error?.response?.data?.error || "Não foi possível carregar seus lembretes de hoje."
      );
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activePanel !== "overview") return;
    loadCalendarHighlights();
  }, [activePanel, loadCalendarHighlights, panelRefreshToken, refreshKey]);

  const loadAdvancedIndicators = useCallback(async () => {
    setAdvancedLoading(true);
    setAdvancedError("");
    const warnings = [];

    try {
      const [openResult, completedResult] = await Promise.allSettled([
        api.get("/processes/instances/open"),
        api.get("/processes/instances/completed", {
          params: {
            limitDays: COMPLETED_ANALYTICS_DAYS,
            limit: COMPLETED_ANALYTICS_LIMIT,
          },
        }),
      ]);

      const openItems =
        openResult.status === "fulfilled" && Array.isArray(openResult.value?.data)
          ? openResult.value.data
          : [];
      const completedItems =
        completedResult.status === "fulfilled" && Array.isArray(completedResult.value?.data)
          ? completedResult.value.data
          : [];

      setGlobalOpenItems(openItems);
      setGlobalCompletedItems(completedItems);

      const openFailed = openResult.status === "rejected";
      const completedFailed = completedResult.status === "rejected";

      if (openFailed) {
        warnings.push("Não foi possível carregar as solicitações em andamento (permissão necessária).");
      }

      if (completedFailed) {
        warnings.push("Não foi possível carregar o histórico de solicitações concluídas.");
      }

      if (openFailed && completedFailed) {
        warnings.push("Os indicadores de solicitações serão exibidos apenas com dados de processos e formulários disponíveis.");
      }
    } catch (error) {
      setGlobalOpenItems([]);
      setGlobalCompletedItems([]);
      setAdvancedError("Não foi possível carregar os indicadores de solicitações.");
      setAdvancedWarnings([]);
      setAdvancedLoading(false);
      return;
    }

    try {
      const res = await api.get("/processes");
      setGlobalProcesses(res.data || []);
    } catch (error) {
      try {
        const fallback = await api.get("/processes/available");
        setGlobalProcesses(fallback.data || []);
        warnings.push("Os totais de processos consideram apenas os fluxos disponíveis para você.");
      } catch (innerError) {
        setGlobalProcesses([]);
        warnings.push("Não foi possível carregar a lista de processos cadastrados.");
      }
    }

    try {
      const res = await api.get("/forms");
      setGlobalForms(res.data || []);
    } catch (error) {
      setGlobalForms([]);
      warnings.push("Não foi possível carregar os formulários publicados (permissão necessária).");
    }

    warnings.push(
      `Os indicadores consideram as solicitações concluídas nos últimos ${COMPLETED_ANALYTICS_DAYS} dias (máximo de ${COMPLETED_ANALYTICS_LIMIT} registros).`
    );
    setAdvancedWarnings(warnings);
    setAdvancedLoading(false);
  }, []);

  useEffect(() => {
    if (activePanel !== "insights") return;
    loadAdvancedIndicators();
  }, [activePanel, loadAdvancedIndicators, panelRefreshToken, refreshKey]);

  const determineAssignmentScopes = useCallback(
    (instance) => {
      const scopes = new Set();
      if (!instance) return scopes;

      const normalizedUserId = normalizeId(user?.id);
      if (!normalizedUserId) return scopes;

      const assigneeId = normalizeId(instance.currentAssignee?.id);
      if (assigneeId && assigneeId !== normalizedUserId) {
        return scopes;
      }

      if ((assigneeId && assigneeId === normalizedUserId) || instance.isCurrentAssignee) {
        scopes.add("mine");
      }

      const requesterId = normalizeId(instance.startedBy?.id);
      const normalizedGroupId = normalizeId(user?.group?.id);
      const normalizedSectorId = normalizeId(user?.sector?.id);

      const responsibleSources = [instance.stepResponsibles, instance.process?.stepResponsibles];

      for (const source of responsibleSources) {
        if (!source) continue;
        const entry = source[String(instance.currentStep)];
        if (!entry) continue;

        const { userIds, groupIds, sectorIds, requester } = extractResponsible(entry);
        const normalizedUserIds = toNormalizedArray(userIds);
        const normalizedGroupIds = toNormalizedArray(groupIds);
        const normalizedSectorIds = toNormalizedArray(sectorIds);

        if (normalizedUserIds.includes(normalizedUserId)) {
          scopes.add("mine");
        }

        if (
          normalizedGroupId &&
          normalizedGroupIds.includes(normalizedGroupId)
        ) {
          scopes.add("sector");
        }

        if (
          normalizedSectorId &&
          normalizedSectorIds.includes(normalizedSectorId)
        ) {
          scopes.add("sector");
        }

        if (
          requester &&
          requesterId &&
          requesterId === normalizedUserId
        ) {
          scopes.add("mine");
        }
      }

      if (!scopes.size && instance.isPotentialResponsible) {
        scopes.add("sector");
      }

      return scopes;
    },
    [extractResponsible, user]
  );

  const itemMetadata = useMemo(() => {
    const scoped = [];
    const scopesMap = new Map();
    const statusMap = new Map();
    const counts = { total: 0, onTime: 0, alert: 0, late: 0 };

    items.forEach((instance) => {
      const scopes = determineAssignmentScopes(instance);
      if (!scopes.size) {
        return;
      }

      scoped.push(instance);
      const key = getInstanceKey(instance);

      if (key) {
        scopesMap.set(key, scopes);
      }

      const { status } = evaluateTaskDeadline(instance);
      if (key) {
        statusMap.set(key, status);
      }

      counts.total += 1;
      if (status === "late") {
        counts.late += 1;
      } else if (status === "alert") {
        counts.alert += 1;
      } else {
        counts.onTime += 1;
      }
    });

    return {
      scopedItems: scoped,
      scopesMap,
      taskCounts: counts,
      taskStatusMap: statusMap,
    };
  }, [determineAssignmentScopes, evaluateTaskDeadline, items]);

  const { scopedItems, scopesMap, taskCounts, taskStatusMap } = itemMetadata;

  const getTaskStatusForInstance = useCallback(
    (instance) => {
      const key = getInstanceKey(instance);
      if (key && taskStatusMap.has(key)) {
        return taskStatusMap.get(key);
      }
      return evaluateTaskDeadline(instance).status;
    },
    [evaluateTaskDeadline, taskStatusMap]
  );

  const matchesTaskFilter = useCallback(
    (instance) => {
      if (taskFilter === "all") {
        return true;
      }

      const status = getTaskStatusForInstance(instance);

      if (taskFilter === "onTime") {
        return status === "onTime";
      }
      if (taskFilter === "alert") {
        return status === "alert";
      }
      if (taskFilter === "late") {
        return status === "late";
      }

      return true;
    },
    [getTaskStatusForInstance, taskFilter]
  );

  const toDeadlineTime = useCallback((value) => {
    if (!value) return Number.POSITIVE_INFINITY;
    const date = new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
  }, []);

  const compareByDeadline = useCallback(
    (a, b) => toDeadlineTime(a?.currentStepDeadline) - toDeadlineTime(b?.currentStepDeadline),
    [toDeadlineTime]
  );

  const getProcessGroupKey = useCallback((instance) => {
    if (!instance) return null;
    const candidateValues = [
      instance.process?.id,
      instance.processId,
      instance.process?.processId,
      instance.process?.key,
      instance.process?.code,
      instance.process?.identifier,
      instance.process?.acronym,
      instance.process?.shortName,
    ];

    for (const value of candidateValues) {
      const normalized = normalizeId(value);
      if (normalized) {
        return `process-${normalized}`;
      }
    }

    const name = typeof instance.process?.name === "string" ? instance.process.name.trim() : "";
    if (name) {
      return `process-name-${name.toLowerCase()}`;
    }

    const fallbackKey = getInstanceKey(instance);
    return fallbackKey ? `instance-${fallbackKey}` : null;
  }, []);

  const groupAssignmentsByProcess = useCallback(
    (assignments) => {
      const map = new Map();

      assignments.forEach((instance) => {
        const key = getProcessGroupKey(instance) || `instance-${instance?.id}`;
        if (!map.has(key)) {
          map.set(key, { key, items: [] });
        }
        map.get(key).items.push(instance);
      });

      const groups = Array.from(map.values());
      groups.forEach((group) => {
        group.items.sort(compareByDeadline);
      });

      groups.sort((a, b) => {
        const earliestA = a.items.length ? toDeadlineTime(a.items[0].currentStepDeadline) : Number.POSITIVE_INFINITY;
        const earliestB = b.items.length ? toDeadlineTime(b.items[0].currentStepDeadline) : Number.POSITIVE_INFINITY;
        return earliestA - earliestB;
      });

      return groups;
    },
    [compareByDeadline, getProcessGroupKey, toDeadlineTime]
  );

  const { directAssignments, sectorAssignments } = useMemo(() => {
    const direct = [];
    const sectorOnly = [];

    items.forEach((instance) => {
      if (!matchesTaskFilter(instance)) {
        return;
      }

      const instanceKey = getInstanceKey(instance);
      const scopes = instanceKey ? scopesMap.get(instanceKey) : determineAssignmentScopes(instance);
      if (scopes?.has("mine")) {
        direct.push(instance);
        return;
      }

      if (scopes?.has("sector")) {
        sectorOnly.push(instance);
      }
    });

    direct.sort(compareByDeadline);
    sectorOnly.sort(compareByDeadline);

    return { directAssignments: direct, sectorAssignments: sectorOnly };
  }, [compareByDeadline, determineAssignmentScopes, items, matchesTaskFilter, scopesMap]);

  const directGroups = useMemo(
    () => groupAssignmentsByProcess(directAssignments),
    [directAssignments, groupAssignmentsByProcess]
  );

  const sectorGroups = useMemo(
    () => groupAssignmentsByProcess(sectorAssignments),
    [groupAssignmentsByProcess, sectorAssignments]
  );

  const directHighlights = useMemo(
    () => directGroups.slice(0, 4),
    [directGroups]
  );

  const sectorHighlights = useMemo(
    () => sectorGroups.slice(0, 4),
    [sectorGroups]
  );

  const hasSectorContext = Boolean(user?.sector?.id);

  const allTrackedInstances = useMemo(
    () => [...globalOpenItems, ...globalCompletedItems],
    [globalCompletedItems, globalOpenItems]
  );

  const aggregatedByMonth = useMemo(() => {
    const map = new Map();
    allTrackedInstances.forEach((instance) => {
      const started = new Date(instance.startedAt);
      if (Number.isNaN(started.getTime())) return;
      const key = `${started.getFullYear()}-${String(started.getMonth() + 1).padStart(2, "0")}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [allTrackedInstances]);

  const aggregatedByDate = useMemo(() => {
    const map = new Map();
    allTrackedInstances.forEach((instance) => {
      const started = new Date(instance.startedAt);
      if (Number.isNaN(started.getTime())) return;
      const key = `${started.getFullYear()}-${String(started.getMonth() + 1).padStart(2, "0")}-${String(
        started.getDate()
      ).padStart(2, "0")}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [allTrackedInstances]);

  const requestsThisMonth = useMemo(() => {
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return aggregatedByMonth.get(key) || 0;
  }, [aggregatedByMonth]);

  const previousMonthCount = useMemo(() => {
    const now = new Date();
    const reference = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const key = `${reference.getFullYear()}-${String(reference.getMonth() + 1).padStart(2, "0")}`;
    return aggregatedByMonth.get(key) || 0;
  }, [aggregatedByMonth]);

  const requestsInPeriod = useMemo(() => {
    if (!recentDays || recentDays <= 0) return 0;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (recentDays - 1));
    const startTime = start.getTime();
    return allTrackedInstances.filter((instance) => {
      const started = new Date(instance.startedAt);
      if (Number.isNaN(started.getTime())) return false;
      return started.getTime() >= startTime;
    }).length;
  }, [allTrackedInstances, recentDays]);

  const monthlyTrend = useMemo(() => {
    const points = [];
    const labels = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
      const count = aggregatedByMonth.get(key) || 0;
      const label = monthDate
        .toLocaleDateString("pt-BR", { month: "short" })
        .replace(".", "")
        .toUpperCase();
      labels.push(label);
      points.push(count);
    }
    const chartWidth = 520;
    const chartHeight = 200;
    const padding = 40;
    const maxValue = Math.max(1, ...points);
    const mappedPoints = points.map((value, index) => {
      const x =
        points.length === 1
          ? chartWidth / 2
          : (index / (points.length - 1)) * (chartWidth - padding * 2) + padding;
      const y =
        chartHeight - padding - (value / maxValue) * Math.max(chartHeight - padding * 2, 1);
      return { x, y, value };
    });
    const areaPath = mappedPoints.length
      ? `M ${mappedPoints[0].x} ${mappedPoints[0].y} ${mappedPoints
          .slice(1)
          .map((p) => `L ${p.x} ${p.y}`)
          .join(" ")} L ${mappedPoints[mappedPoints.length - 1].x} ${chartHeight - padding} L ${
          mappedPoints[0].x
        } ${chartHeight - padding} Z`
      : "";
    return {
      labels,
      points: mappedPoints,
      areaPath,
      chart: { width: chartWidth, height: chartHeight, padding },
      total: points.reduce((acc, curr) => acc + curr, 0),
    };
  }, [aggregatedByMonth]);

  const periodTrend = useMemo(() => {
    const windowDays = Math.min(Math.max(recentDays, 1), 30);
    const now = new Date();
    const buckets = [];
    for (let i = windowDays - 1; i >= 0; i -= 1) {
      const day = new Date(now);
      day.setHours(0, 0, 0, 0);
      day.setDate(now.getDate() - i);
      const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(
        day.getDate()
      ).padStart(2, "0")}`;
      const count = aggregatedByDate.get(key) || 0;
      const label = day
        .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
        .replace(".", "");
      buckets.push({ label, count });
    }
    const max = Math.max(...buckets.map((bucket) => bucket.count), 1);
    const total = buckets.reduce((acc, bucket) => acc + bucket.count, 0);
    return { buckets, max, windowDays, total };
  }, [aggregatedByDate, recentDays]);

  const avgCompletionAll = useMemo(() => {
    const durations = globalCompletedItems
      .filter((instance) => instance.endedAt)
      .map((instance) => {
        const start = new Date(instance.startedAt).getTime();
        const end = new Date(instance.endedAt).getTime();
        if (Number.isNaN(start) || Number.isNaN(end)) return null;
        return Math.max(end - start, 0);
      })
      .filter(Boolean);
    if (!durations.length) return null;
    return durations.reduce((acc, curr) => acc + curr, 0) / durations.length;
  }, [globalCompletedItems]);

  const formsWithExternal = useMemo(() => {
    return globalForms.filter((form) => form?.externalAccessEnabled).length;
  }, [globalForms]);

  const processCategoryDistribution = useMemo(() => {
    if (!globalProcesses.length) return [];
    const accumulator = globalProcesses.reduce((acc, process) => {
      const key = process?.category?.trim() || "Sem categoria";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const entries = Object.entries(accumulator).sort((a, b) => b[1] - a[1]);
    const total = globalProcesses.length || 1;
    return entries.map(([name, value]) => ({
      name,
      value,
      percentage: Math.round((value / total) * 100),
    }));
  }, [globalProcesses]);

  const topProcessesByVolume = useMemo(() => {
    if (!allTrackedInstances.length) return [];
    const accumulator = allTrackedInstances.reduce((acc, instance) => {
      const key = instance.process?.name || "Processo";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const entries = Object.entries(accumulator).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((acc, [, value]) => acc + value, 0) || 1;
    return entries.slice(0, 6).map(([name, value]) => ({
      name,
      value,
      percentage: Math.round((value / total) * 100),
    }));
  }, [allTrackedInstances]);

  const globalStatusBreakdown = useMemo(() => {
    if (!globalOpenItems.length) {
      return [
        { label: "Sem solicitações em aberto", value: 0, color: "bg-blue-200", percentage: 0 },
      ];
    }
    const withDeadline = globalOpenItems.filter((instance) => instance.currentStepDeadline);
    const onTime = withDeadline.filter((instance) => {
      const deadline = new Date(instance.currentStepDeadline);
      return !Number.isNaN(deadline.getTime()) && deadline.getTime() >= Date.now();
    }).length;
    const late = withDeadline.length - onTime;
    const noDeadline = globalOpenItems.length - withDeadline.length;
    const total = globalOpenItems.length || 1;
    return [
      {
        label: "Dentro do prazo",
        value: onTime,
        color: "bg-emerald-500",
        percentage: Math.round((onTime / total) * 100),
      },
      {
        label: "Em atraso",
        value: late,
        color: "bg-rose-500",
        percentage: Math.round((late / total) * 100),
      },
      {
        label: "Sem prazo",
        value: noDeadline,
        color: "bg-slate-400",
        percentage: Math.round((noDeadline / total) * 100),
      },
    ];
  }, [globalOpenItems]);

  const monthDeltaDescription = useMemo(() => {
    if (previousMonthCount === 0) {
      if (requestsThisMonth === 0) {
        return "Sem movimentações registradas";
      }
      return "Sem histórico para comparação";
    }
    const diff = requestsThisMonth - previousMonthCount;
    const percentage = Math.round((diff / previousMonthCount) * 100);
    const sign = diff > 0 ? "+" : "";
    return `${sign}${diff} vs mês anterior (${sign}${percentage}%)`;
  }, [previousMonthCount, requestsThisMonth]);

  const averageCompletionLabel = useMemo(() => {
    if (!avgCompletionAll) return "Sem histórico suficiente";
    return `Tempo médio de ciclo: ${formatDuration(avgCompletionAll)}`;
  }, [avgCompletionAll]);

  const topCategoryDescription = useMemo(() => {
    if (!processCategoryDistribution.length) return "Categorias serão exibidas ao carregar os processos.";
    const [top] = processCategoryDistribution;
    return `${top.percentage}% dos processos estão em ${top.name.toLowerCase()}`;
  }, [processCategoryDistribution]);

  const formsCardDescription = useMemo(() => {
    if (!globalForms.length) return "Carregue formulários para visualizar detalhes.";
    if (!formsWithExternal) return "Nenhum formulário com acesso externo ativo.";
    return `${formsWithExternal} com acesso externo ativo`;
  }, [formsWithExternal, globalForms.length]);

  const advancedCards = useMemo(
    () => [
      {
        label: "Solicitações no mês",
        value: requestsThisMonth,
        icon: <FaCalendarAlt className="text-2xl" />,
        description: monthDeltaDescription,
      },
      {
        label: `Novas solicitações (${recentDays}d)`,
        value: requestsInPeriod,
        icon: <FaChartBar className="text-2xl" />,
        description: averageCompletionLabel,
      },
      {
        label: "Processos monitorados",
        value: globalProcesses.length,
        icon: <FaCogs className="text-2xl" />,
        description: topCategoryDescription,
      },
      {
        label: "Formulários publicados",
        value: globalForms.length,
        icon: <FaWpforms className="text-2xl" />,
        description: formsCardDescription,
      },
    ],
    [
      averageCompletionLabel,
      formsCardDescription,
      globalForms.length,
      globalProcesses.length,
      monthDeltaDescription,
      recentDays,
      requestsInPeriod,
      requestsThisMonth,
      topCategoryDescription,
    ]
  );

  const handleRecentDaysChange = (event) => {
    const value = Number(event.target.value);
    if (Number.isNaN(value) || value <= 0) return;
    setRecentDays(Math.min(Math.max(Math.round(value), 1), 90));
  };

  const openInstance = (inst) => {
    if (openTab) {
      openTab(
        `instance-${inst.id}`,
        `Solicitação ${inst.id}`,
        ProcessRunner,
        { instanceId: inst.id, returnToTabId: "home" }
      );
    }
  };

  const handleClearTaskFilter = useCallback(() => {
    setTaskFilter("all");
  }, [setTaskFilter]);

  const getProcessIdentifierLabel = (item) => {
    const rawIdentifier =
      item?.process?.identifier ||
      item?.process?.code ||
      item?.process?.key ||
      item?.process?.acronym ||
      item?.process?.shortName ||
      null;

    if (typeof rawIdentifier === "string" && rawIdentifier.trim()) {
      return rawIdentifier.trim();
    }

    const rawId = item?.process?.id ?? item?.processId ?? item?.process?.processId ?? null;

    if (rawId === null || rawId === undefined) {
      return null;
    }

    const normalized = String(rawId).trim();
    if (!normalized) {
      return null;
    }

    if (/^\d+$/.test(normalized)) {
      return null;
    }

    return normalized.startsWith("#") ? normalized : `#${normalized}`;
  };

  const getHighlightInfo = (item) => {
    const highlightValue = (() => {
      const raw = item?.dashboardHighlight?.value;
      if (typeof raw !== "string") return null;
      const trimmed = raw.trim();
      return trimmed || null;
    })();

    const highlightLabel = (() => {
      const raw = item?.dashboardHighlight?.label;
      if (typeof raw !== "string") return null;
      const trimmed = raw.trim();
      return trimmed || null;
    })();

    const displayLabel = highlightLabel || "Destaque";
    const title = highlightLabel ? `${highlightLabel}: ${highlightValue}` : highlightValue || undefined;

    return { value: highlightValue, label: displayLabel, title };
  };

  const getDeadlineLabel = (item) => {
    if (!item?.currentStepDeadline) {
      return null;
    }

    const deadlineDate = new Date(item.currentStepDeadline);
    if (Number.isNaN(deadlineDate.getTime())) {
      return null;
    }

    const datePart = deadlineDate.toLocaleDateString("pt-BR");
    const timePart = deadlineDate.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart} às ${timePart}`;
  };

  const getStatusStyles = (status) => {
    const normalizedStatusLabel = status.label?.toLowerCase?.() || "";
    const isCompactBadge =
      normalizedStatusLabel === "prazo excedido" ||
      normalizedStatusLabel === "sem prazo" ||
      normalizedStatusLabel.startsWith("vence em");

    const base = `${
      isCompactBadge
        ? "border border-blue-500/60 bg-white/80 text-blue-800"
        : "border bg-transparent"
    } backdrop-blur-sm shadow-sm`;

    let statusBadgeClass = base;
    if (!isCompactBadge) {
      if (status.color?.includes("rose")) {
        statusBadgeClass = `${base} border-rose-600 text-rose-700`;
      } else if (status.color?.includes("amber")) {
        statusBadgeClass = `${base} border-amber-600 text-amber-700`;
      } else if (status.color?.includes("emerald")) {
        statusBadgeClass = `${base} border-emerald-600 text-emerald-700`;
      } else {
        statusBadgeClass = `${base} border-sky-600 text-sky-700`;
      }
    }

    const deadlineTextClass = (() => {
      if (status.color?.includes("rose")) {
        return "text-rose-600";
      }
      if (status.color?.includes("amber")) {
        return "text-amber-600";
      }
      if (status.color?.includes("emerald")) {
        return "text-emerald-600";
      }
      return "text-blue-700";
    })();

    return { statusBadgeClass, isCompactBadge, deadlineTextClass };
  };

  const renderProcessGroup = (group) => {
    if (!group?.items?.length) {
      return null;
    }

    const representative = group.items[0];
    const groupKey =
      group.key || getProcessGroupKey(representative) || getInstanceKey(representative) || `instance-${representative?.id}`;
    const itemsCount = group.items.length;
    const isExpandable = itemsCount > 1;
    const isExpanded = !isExpandable || expandedGroupKeys.has(groupKey);
    const hasMultipleItems = itemsCount > 1;

    const processIdentifierLabel = getProcessIdentifierLabel(representative);

    if (!isExpandable) {
      const status = getDeadlineStatus(representative);
      const { value: highlightValue, label: highlightDisplayLabel, title: highlightTitle } =
        getHighlightInfo(representative);
      const { statusBadgeClass, isCompactBadge } = getStatusStyles(status);

      return (
        <div key={groupKey} className="dashboard-process-wrapper rounded-3xl bg-blue-200 p-[1.5px]">
          <div className="dashboard-process-card group flex flex-col overflow-hidden rounded-2xl border border-blue-50/80 bg-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg">
            <div className="relative -mx-[1.5px] -mt-[1.5px] overflow-hidden rounded-t-2xl bg-gradient-to-r from-blue-500/40 via-blue-500/20 to-transparent px-4 py-2 text-blue-900">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <h4 className="dashboard-process-name text-lg font-semibold leading-snug text-blue-950">
                    {representative.process?.name || "Processo"}
                  </h4>
                  {processIdentifierLabel && (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-600/90">
                      {processIdentifierLabel}
                    </span>
                  )}
                  {hasMultipleItems && (
                    <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-blue-800">
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] text-blue-600">
                        {itemsCount} {itemsCount === 1 ? "tarefa" : "tarefas"}
                      </span>
                    </span>
                  )}
                  {highlightValue && (
                    <span
                      title={highlightTitle}
                      className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-blue-100 bg-white/40 px-2 py-0.5 text-[10px] font-semibold text-blue-800 backdrop-blur-sm"
                    >
                      <span className="text-[8px] font-semibold uppercase tracking-[0.35em] text-blue-500">
                        {highlightDisplayLabel}
                      </span>
                      <span className="max-w-[13rem] truncate">{highlightValue}</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`inline-flex items-center rounded-full ${
                      isCompactBadge ? "px-1 text-[8px]" : "px-2 text-[10px]"
                    } py-0.5 font-semibold uppercase tracking-[0.25em] ${statusBadgeClass}`}
                  >
                    {status.label}
                  </span>
                </div>
              </div>
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/40 blur-2xl transition group-hover:scale-110" />
            </div>
            <div className="divide-y divide-blue-100/80 bg-blue-50/40">
              {group.items.map((item) => {
                const itemStatus = getDeadlineStatus(item);
                const { statusBadgeClass: itemBadgeClass, isCompactBadge: itemCompact, deadlineTextClass: itemDeadlineClass } =
                  getStatusStyles(itemStatus);
                const itemHighlight = getHighlightInfo(item);
                const itemStartedAt = new Date(item.startedAt);
                const itemStartedLabel = Number.isNaN(itemStartedAt.getTime()) ? "-" : itemStartedAt.toLocaleString();
                const hasItemDeadline = Boolean(item?.currentStepDeadline);
                const itemDeadlineLabel = hasItemDeadline ? getDeadlineLabel(item) : itemStatus.label;

                const showItemStatusBadge = hasMultipleItems && itemStatus.label !== NO_DEADLINE_LABEL;
                const showItemHighlight = hasMultipleItems && itemHighlight.value;
                const showItemDeadline = hasItemDeadline || hasMultipleItems;

                return (
                  <div
                    key={item.id}
                    className="flex flex-col gap-3 px-4 py-3 text-[11px] text-slate-600 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50/70 px-2 py-0.5 font-semibold text-blue-700">
                        <span className="font-bold text-blue-800">#{String(item.id).padStart(4, "0")}</span>
                      </span>
                      {showItemStatusBadge && (
                        <span
                          className={`inline-flex items-center rounded-full ${
                            itemCompact ? "px-1 text-[8px]" : "px-2 text-[10px]"
                          } py-0.5 font-semibold uppercase tracking-[0.25em] ${itemBadgeClass}`}
                        >
                          {itemStatus.label}
                        </span>
                      )}
                      {showItemHighlight && (
                        <span
                          title={itemHighlight.title}
                          className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-blue-100 bg-white/60 px-2 py-0.5 text-[10px] font-semibold text-blue-800 backdrop-blur"
                        >
                          <span className="text-[8px] font-semibold uppercase tracking-[0.35em] text-blue-500">
                            {itemHighlight.label}
                          </span>
                          <span className="max-w-[12rem] truncate">{itemHighlight.value}</span>
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 text-slate-700">
                        <FaCalendarAlt className="text-blue-500" />
                        <span>
                          <span className="font-semibold text-slate-900">Início:</span> {itemStartedLabel}
                        </span>
                      </span>
                      {showItemDeadline && itemDeadlineLabel && (
                        <span className={`inline-flex items-center gap-1.5 ${itemDeadlineClass}`}>
                          <FaStopwatch className={`text-[13px] ${itemDeadlineClass}`} />
                          <span>
                            <span className="font-semibold">Prazo:</span> {itemDeadlineLabel}
                          </span>
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openInstance(item)}
                      className="dashboard-task-open-button inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
                    >
                      <FaRegHandPaper className="text-sm" />
                      Abrir
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={groupKey} className="dashboard-process-wrapper rounded-3xl bg-blue-200 p-[1.5px]">
        <div className="dashboard-process-card flex flex-col overflow-hidden rounded-2xl border border-blue-50/80 bg-white shadow-md">
          <button
            type="button"
            onClick={() => toggleGroupExpansion(groupKey)}
            aria-expanded={isExpanded}
            className="relative flex items-center justify-between gap-3 bg-gradient-to-r from-blue-500/40 via-blue-500/20 to-transparent px-4 py-3 text-left text-blue-950 transition hover:bg-blue-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2"
          >
            <div className="flex flex-col gap-1">
              <h4 className="dashboard-process-name text-lg font-semibold leading-snug">{representative.process?.name || "Processo"}</h4>
              {processIdentifierLabel && (
                <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-700/90">
                  {processIdentifierLabel}
                </span>
              )}
              {itemsCount > 1 && (
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-blue-800">
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] text-blue-600">
                    {itemsCount} tarefas
                  </span>
                </span>
              )}
            </div>
            <span
              className={`inline-flex items-center justify-center rounded-full border border-blue-200 bg-white/70 p-1 text-blue-700 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            >
              <FaChevronDown className="text-xs" />
            </span>
          </button>
          <div className={`${isExpanded ? "block" : "hidden"}`}>
            {group.items.map((item) => {
              const itemStatus = getDeadlineStatus(item);
              const { deadlineTextClass: itemDeadlineClass } = getStatusStyles(itemStatus);
              const itemHighlight = getHighlightInfo(item);
              const itemStartedAt = new Date(item.startedAt);
              const itemStartedLabel = Number.isNaN(itemStartedAt.getTime()) ? "-" : itemStartedAt.toLocaleString();
              const itemDeadlineLabel = getDeadlineLabel(item) || itemStatus.label;

              return (
                <div
                  key={item.id}
                  className="border-t border-blue-100 bg-white px-4 py-3 text-[11px] text-slate-600"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-700">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center justify-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        #{String(item.id).padStart(4, "0")}
                      </span>
                      {itemHighlight.value && (
                        <span
                          title={itemHighlight.title}
                          className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50/80 px-2 py-0.5 text-[10px] font-semibold text-blue-800"
                        >
                          <span className="text-[8px] font-semibold uppercase tracking-[0.35em] text-blue-500">
                            {itemHighlight.label}
                          </span>
                          <span className="max-w-[12rem] truncate">{itemHighlight.value}</span>
                        </span>
                      )}
                    </div>
                    {itemStatus.label !== NO_DEADLINE_LABEL && (
                      <span className={`inline-flex items-center gap-1 font-semibold ${itemDeadlineClass}`}>
                        <FaStopwatch className={`text-[13px] ${itemDeadlineClass}`} />
                        {itemStatus.label}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                      <FaCalendarAlt className="text-blue-500" />
                      <span>
                        <span className="font-semibold text-slate-900">Início:</span> {itemStartedLabel}
                      </span>
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 ${itemDeadlineClass}`}>
                        <FaStopwatch className={`text-[13px] ${itemDeadlineClass}`} />
                        <span>
                          <span className="font-semibold">Prazo:</span> {itemDeadlineLabel}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => openInstance(item)}
                        className="dashboard-task-open-button inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
                      >
                        <FaRegHandPaper className="text-sm" />
                        Abrir
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderAssignmentCard = (item) => {
    const group = Array.isArray(item?.items) ? item : { key: item?.id ?? getInstanceKey(item), items: [item] };
    return renderProcessGroup(group);
  };

  const firstName = user?.name ? user.name.split(" ")[0] : "";

  const metricCards = [
    {
      id: "all",
      label: "Tarefas",
      value: formatNumber(taskCounts.total),
      icon: <FaTasks className="dashboard-metric-icon dashboard-metric-icon--tasks text-2xl text-blue-500" />,
      description: "Todas as tarefas em aberto",
    },
    {
      id: "onTime",
      label: "No prazo",
      value: formatNumber(taskCounts.onTime),
      icon: <FaCheckCircle className="dashboard-metric-icon dashboard-metric-icon--on-time text-2xl text-emerald-500" />,
      description: "Dentro do SLA definido",
    },
    {
      id: "alert",
      label: "Em alerta",
      value: formatNumber(taskCounts.alert),
      icon: <FaExclamationTriangle className="dashboard-metric-icon dashboard-metric-icon--alert text-2xl text-amber-400" />,
      description: "Prazo próximo do vencimento",
    },
    {
      id: "late",
      label: "Atrasado",
      value: formatNumber(taskCounts.late),
      icon: <FaTimesCircle className="dashboard-metric-icon dashboard-metric-icon--late text-2xl text-red-500" />,
      description: "Prazo excedido",
    },
  ];

  const panelOptions = [
    {
      id: "overview",
      label: "Visão operacional",
      icon: <FaGlobeAmericas className="text-base" />,
    },
    {
      id: "insights",
      label: "Indicadores avançados",
      icon: <FaChartLine className="text-base" />,
    },
  ];

  const hasFilterApplied = taskFilter !== "all";

  const topTodayMessages = useMemo(() => todayMessages.slice(0, 3), [todayMessages]);
  const extraTodayMessages = Math.max(todayMessages.length - topTodayMessages.length, 0);

  return (
    <div className="space-y-10 bg-white px-4 py-6 text-slate-800 sm:px-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Olá{firstName ? `, ${firstName}` : ""}! Acompanhe suas tarefas.
          </h1>
        </div>
        {hasFilterApplied && (
          <button
            type="button"
            onClick={handleClearTaskFilter}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
            title="Limpar filtros"
          >
            <span className="sr-only">Limpar filtros</span>
            <FaBroom className="text-lg" />
          </button>
        )}
      </header>

      <div className="flex flex-wrap gap-2">
        {panelOptions.map(
          (option) => (
            <button
              key={option.id}
              onClick={() => handlePanelSelect(option.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                activePanel === option.id
                  ? "bg-slate-900 text-white shadow"
                  : "border border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <span className="flex items-center gap-2">
                {option.icon}
                <span>{option.label}</span>
              </span>
            </button>
          )
        )}
      </div>

      {activePanel === "overview" && (
        <>
          <section className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 xl:grid-cols-6">
            <div className="flex h-full flex-col rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm lg:col-span-2 xl:col-span-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">
                    lembretes da agenda
                  </p>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Próximos lembretes
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => openTab("agenda", "Agenda", Agenda, { openTab })}
                  className="dashboard-agenda-open-button inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-white px-3 py-1 text-[11px] font-medium text-emerald-700 transition hover:bg-emerald-50"
                >
                  <FaCalendarAlt className="text-xs" />
                  Abrir
                </button>
              </div>
              <div className="mt-3 flex-1">
                {calendarLoading ? (
                  <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
                    Carregando lembretes...
                  </div>
                ) : calendarError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-600">
                    {calendarError}
                  </div>
                ) : topTodayMessages.length ? (
                  <ul className="space-y-2 text-sm text-emerald-700">
                    {topTodayMessages.map((message, index) => (
                      <li
                        key={`${message}-${index}`}
                        className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-3"
                      >
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                          <FaBell className="text-[10px]" />
                        </span>
                        <span>{message}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-700">
                    Nenhum lembrete para as próximas 48 horas.
                  </div>
                )}
              </div>
              {extraTodayMessages > 0 && (
                <p className="mt-2 text-[11px] font-medium text-emerald-600/80">
                  +{extraTodayMessages} lembretes adicionais disponíveis na agenda
                </p>
              )}
            </div>

            {metricCards.map((card, index) => {
              const isActive = taskFilter === card.id;
              const theme = getMetricCardTheme(card.id, index);
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setTaskFilter(card.id)}
                  aria-pressed={isActive}
                  title={card.description}
                  className={`dashboard-metric-card relative block w-full self-start overflow-hidden rounded-2xl bg-gradient-to-br ${theme.background} p-6 text-left ${theme.textColor} transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${theme.focusRing} lg:col-span-1 xl:col-span-1 ${
                    isActive
                      ? `ring-2 ${theme.activeRing} ring-offset-2 shadow-2xl`
                      : `ring-1 ${theme.inactiveRing} shadow-md hover:shadow-xl`
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-semibold uppercase tracking-wide ${theme.labelColor}`}>
                        {card.label}
                      </p>
                      <p className="mt-3 text-4xl font-bold">{card.value}</p>
                    </div>
                    <div className={`rounded-xl p-3 shadow-inner ${theme.iconWrapper}`}>
                      {card.icon}
                    </div>
                  </div>
                </button>
              );
            })}
          </section>

          <section className="dashboard-tasks-shell rounded-3xl border border-blue-100 bg-blue-50 p-6 shadow-sm">
            {tasksLoading ? (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-center text-sm font-medium text-blue-700">
                Carregando suas tarefas...
              </div>
            ) : directHighlights.length || (hasSectorContext && sectorHighlights.length) ? (
              <div
                className={`grid gap-4 ${hasSectorContext ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}
              >
                <div className="dashboard-tasks-column flex flex-col gap-4 rounded-3xl border border-blue-100 bg-white p-4 shadow-sm">
                  <div className="space-y-2">
                    <div className="flex w-full flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">Suas tarefas</h3>
                      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-500">
                        {directAssignments.length
                          ? formatNumber(directAssignments.length)
                          : "0"}
                      </span>
                    </div>
                    {directAssignments.length === 0 && (
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-500">
                        {hasFilterApplied
                          ? "Nenhuma tarefa no filtro selecionado"
                          : "Nenhuma tarefa em aberto"}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    {directHighlights.length ? (
                      directHighlights.map((item) => renderAssignmentCard(item))
                    ) : (
                      <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-600">
                        {hasFilterApplied
                          ? "Nenhuma tarefa no filtro selecionado."
                          : "Nenhuma tarefa."}
                      </div>
                    )}
                  </div>
                </div>
                {hasSectorContext && (
                  <div className="dashboard-tasks-column flex flex-col gap-4 rounded-3xl border border-blue-100 bg-white p-4 shadow-sm">
                    <div className="space-y-2">
                      <div className="flex w-full flex-wrap items-baseline justify-between gap-2">
                        <h3 className="text-lg font-semibold text-slate-900">Tarefas do setor</h3>
                        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-500">
                          {sectorAssignments.length
                            ? formatNumber(sectorAssignments.length)
                            : "0"}
                        </span>
                      </div>
                      {sectorAssignments.length === 0 && (
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-500">
                          {hasFilterApplied
                            ? "Nenhuma tarefa no filtro selecionado para o setor"
                            : "Nenhuma tarefa em aberto no setor"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      {sectorHighlights.length ? (
                        sectorHighlights.map((item) => renderAssignmentCard(item))
                      ) : (
                        <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-600">
                          {hasFilterApplied
                            ? "Nenhuma tarefa no filtro selecionado para o seu setor."
                            : "Nenhuma tarefa pendente para o seu setor."}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50 p-6 text-center text-blue-600">
                {hasFilterApplied
                  ? "Nenhuma tarefa no filtro selecionado. Ajuste o filtro para visualizar outras tarefas."
                  : "Nenhuma tarefa pendente. Aproveite para revisar indicadores e processos concluídos."}
              </div>
            )}
          </section>
        </>
      )}

      {activePanel === "insights" && (
        <div className="space-y-8">
          {advancedLoading && (
            <div className="rounded-3xl border border-blue-100 bg-blue-50 p-6 text-center text-blue-700 shadow-sm">
              Carregando indicadores avançados...
            </div>
          )}

          {advancedError && !advancedLoading && (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-600 shadow-sm">
              {advancedError}
            </div>
          )}

          {!advancedError && (
            <>
              {advancedWarnings.length > 0 && (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700 shadow-sm">
                  <ul className="list-disc space-y-1 pl-4">
                    {advancedWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {advancedCards.map((card) => (
                  <div
                    key={card.label}
                    className="relative overflow-hidden rounded-2xl border border-blue-100 bg-white p-5 text-slate-900 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-blue-500">
                          {card.label}
                        </p>
                        <p className="mt-2 text-3xl font-semibold">{formatNumber(card.value)}</p>
                        <p className="mt-1 text-xs text-slate-500">{card.description}</p>
                      </div>
                      <div className="rounded-xl bg-blue-50 p-3 text-blue-600 shadow-inner">
                        {card.icon}
                      </div>
                    </div>
                  </div>
                ))}
              </section>

              <section className="grid gap-6 lg:grid-cols-2 xl:grid-cols-12">
                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6 lg:col-span-1 xl:col-span-7">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                        visão histórica
                      </p>
                      <h2 className="text-xl font-semibold text-slate-900">Solicitações por mês</h2>
                    </div>
                    <span className="self-start rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600 sm:self-auto">
                      {formatNumber(monthlyTrend.total)} movimentos em 6 meses
                    </span>
                  </div>

                  <div className="mt-6">
                    {monthlyTrend.points.length ? (
                      <div>
                        <svg
                          viewBox={`0 0 ${monthlyTrend.chart.width} ${monthlyTrend.chart.height}`}
                          className="h-auto w-full"
                          preserveAspectRatio="xMidYMid meet"
                        >
                          <defs>
                            <linearGradient id="monthlyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(191, 219, 254, 0.9)" />
                              <stop offset="100%" stopColor="rgba(191, 219, 254, 0.2)" />
                            </linearGradient>
                          </defs>
                          {monthlyTrend.areaPath && (
                            <path d={monthlyTrend.areaPath} fill="url(#monthlyGradient)" stroke="none" />
                          )}
                          <polyline
                            points={monthlyTrend.points.map((point) => `${point.x},${point.y}`).join(" ")}
                            fill="none"
                            stroke="rgba(59, 130, 246, 0.6)"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                          {monthlyTrend.points.map((point, index) => (
                            <g key={`${point.x}-${index}`}>
                              <circle
                                cx={point.x}
                                cy={point.y}
                                r={6}
                                fill="rgba(59, 130, 246, 0.9)"
                                stroke="rgba(191, 219, 254, 0.9)"
                                strokeWidth="2"
                              />
                              <text
                                x={point.x}
                                y={point.y - 14}
                                textAnchor="middle"
                                className="fill-blue-700 text-[10px] font-semibold"
                              >
                                {point.value}
                              </text>
                            </g>
                          ))}
                        </svg>
                        <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wide text-blue-600 sm:flex sm:items-center sm:justify-between sm:text-xs">
                          {monthlyTrend.labels.map((label, index) => (
                            <span key={`${label}-${index}`}>{label}</span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-blue-200 bg-blue-50 text-blue-600">
                        Aguarde movimentações para visualizar a tendência mensal.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6 lg:col-span-1 xl:col-span-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                        janela dinâmica
                      </p>
                      <h2 className="text-xl font-semibold text-slate-900">Últimos movimentos</h2>
                      <p className="text-sm text-slate-500">
                        Ajuste a quantidade de dias para observar picos de entrada.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <FaStopwatch className="text-blue-500" />
                      <span>Dias</span>
                      <input
                        type="number"
                        min="1"
                        max="90"
                        value={recentDays}
                        onChange={handleRecentDaysChange}
                        className="w-16 rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:w-20"
                      />
                    </label>
                  </div>

                  <div className="mt-6">
                    {periodTrend.buckets.length ? (
                      <div className="pb-2">
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(3.5rem,1fr))] items-end gap-3">
                          {periodTrend.buckets.map((bucket) => {
                            const height = periodTrend.max
                              ? Math.max((bucket.count / periodTrend.max) * 120, 4)
                              : 4;
                            return (
                              <div
                                key={bucket.label}
                                className="flex flex-col items-center justify-end text-center"
                              >
                                <div className="flex h-32 w-full max-w-[2.75rem] items-end justify-center rounded-full bg-blue-50">
                                  <div
                                    className="w-3/4 rounded-full bg-gradient-to-t from-blue-500 to-sky-400"
                                    style={{ height: `${height}px` }}
                                    title={`${bucket.count} solicitações`}
                                  />
                                </div>
                                <p className="mt-2 text-xs font-medium uppercase tracking-wide text-blue-600">
                                  {bucket.label}
                                </p>
                                <p className="text-xs text-slate-500">{formatNumber(bucket.count)}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-blue-200 bg-blue-50 text-blue-600">
                        Nenhuma nova solicitação no período selecionado.
                      </div>
                    )}
                  </div>

                  <p className="mt-4 text-xs uppercase tracking-[0.25em] text-slate-400">
                    {formatNumber(periodTrend.total)} solicitações nos últimos {periodTrend.windowDays} dias
                  </p>
                </div>
              </section>

              <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    setores do bpm
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">Processos por categoria</h2>
                  <div className="mt-6 space-y-4">
                    {processCategoryDistribution.length ? (
                      processCategoryDistribution.map((item) => (
                        <div key={item.name}>
                          <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                            <span>{item.name}</span>
                            <span>{formatNumber(item.value)}</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-blue-100">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700"
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                            {item.percentage}% do portfólio monitorado
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">
                        Os processos serão exibidos conforme as permissões de acesso disponíveis.
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    fluxos em destaque
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">Processos mais movimentados</h2>
                  <div className="mt-6 space-y-4">
                    {topProcessesByVolume.length ? (
                      topProcessesByVolume.map((item) => (
                        <div key={item.name}>
                          <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                            <span>{item.name}</span>
                            <span>{formatNumber(item.value)}</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-blue-100">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-blue-700"
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                            {item.percentage}% das solicitações monitoradas
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">
                        Assim que novas solicitações forem registradas elas aparecerão aqui.
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6">
                  <p className="text-xs uppercase tracking-[0.35em] text-blue-500/70">
                    saúde operacional
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">Status das filas atuais</h2>
                  <div className="mt-6 space-y-4">
                    {globalStatusBreakdown.map((item) => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                          <span>{item.label}</span>
                          <span>{formatNumber(item.value)}</span>
                        </div>
                        <div className="mt-2 h-2 w-full rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${item.color}`}
                            style={{ width: `${Math.min(item.percentage || 0, 100)}%` }}
                          />
                        </div>
                        <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">
                          {item.percentage || 0}% das solicitações abertas
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

            </>
          )}
        </div>
      )}


    </div>
  );
}
